/**
 * Text Extraction Utilities
 * 
 * Robust sentence extraction and headline/description parsing for V3 analysis data.
 * Handles edge cases like abbreviations, quotes, and formatting variations.
 */

/**
 * PLA-FIX: Metadata leakage pattern for frontend defensive guard
 * Matches internal audit/reconciliation language that should never appear in UX
 */
const METADATA_LEAK_PATTERN = /\bcanon\b|\breconciliation\b|\bpromoted as primary\b|\bintervention target\b|\bresolved as\b/i;

/**
 * PLA-FIX: Check if text contains metadata leakage
 * 
 * This is a defensive frontend guard - a secondary safety net.
 * The backend should never allow metadata to reach the frontend, but if it does,
 * this function allows the frontend to detect and block it.
 * 
 * @param text - Text to check for metadata leakage
 * @returns true if metadata leakage is detected, false otherwise
 */
export function hasMetadataLeakage(text: string | null | undefined): boolean {
  if (!text || typeof text !== 'string') {
    return false;
  }
  return METADATA_LEAK_PATTERN.test(text);
}

/**
 * PLA-FIX: Sanitize text by returning null if metadata leakage detected
 * 
 * This is a defensive frontend guard that blocks leaked metadata from being rendered.
 * If metadata leakage is detected, returns null so the UI can hide the section
 * or show a fallback.
 * 
 * @param text - Text to check and potentially sanitize
 * @param logContext - Optional context for logging (e.g., runId)
 * @returns Original text if clean, null if metadata leakage detected
 */
export function sanitizeForUX(
  text: string | null | undefined, 
  logContext?: { runId?: string; field?: string }
): string | null {
  if (!text || typeof text !== 'string') {
    return null;
  }
  
  if (hasMetadataLeakage(text)) {
    console.warn('[UX GUARD] Metadata leakage blocked from rendering', {
      ...logContext,
      sample: text.substring(0, 100)
    });
    return null;
  }
  
  return text;
}

/**
 * Extract first meaningful sentence from text
 * 
 * Uses regex-based sentence tokenization that handles:
 * - Abbreviations (Dr., etc.)
 * - Quotes and parentheses
 * - Multiple sentence terminators
 * 
 * @param text - Input text to extract sentence from
 * @returns First sentence (min 20 chars) or null if extraction fails
 */
export function extractFirstSentence(text: string | null | undefined): string | null {
  if (!text || typeof text !== 'string' || !text.trim()) {
    return null;
  }

  const trimmed = text.trim();
  
  // Handle single sentence case
  if (trimmed.length < 20) {
    return null; // Too short to be meaningful
  }

  // Sentence splitting regex: matches sentence terminators followed by whitespace and capital letter
  // Pattern requires capital letter followed by lowercase (to avoid splitting on abbreviations like "Dr.")
  // This handles most cases including abbreviations (Dr., Mr., etc.)
  const sentencePattern = /(?<=[.!?])\s+(?=[A-Z][a-z])/;
  let sentences = trimmed.split(sentencePattern).filter(s => s.trim().length > 0);
  
  // Handle case where first "sentence" is too short (likely an abbreviation like "Dr.")
  // Merge it with the next sentence
  if (sentences.length > 1 && sentences[0].trim().length < 10) {
    sentences[0] = sentences[0] + ' ' + sentences[1];
    sentences.splice(1, 1);
  }
  
  // If split didn't work (e.g., single sentence or no matches), return full text if meaningful
  if (sentences.length === 0) {
    return trimmed.length >= 20 ? trimmed : null;
  }

  const firstSentence = sentences[0].trim();
  
  // Ensure minimum length
  if (firstSentence.length < 20) {
    return null;
  }

  // Ensure proper punctuation
  if (!/[.!?]$/.test(firstSentence)) {
    return firstSentence + '.';
  }

  return firstSentence;
}

/**
 * Extract headline from justification text
 * 
 * Returns first sentence as headline, ensuring it's meaningful and distinct.
 * 
 * @param justification - Full justification text from root_causes
 * @returns First sentence as headline, or null if extraction fails
 */
export function extractHeadline(justification: string | null | undefined): string | null {
  return extractFirstSentence(justification);
}

/**
 * Extract description from justification text
 * 
 * Returns full justification as description, ensuring it differs from headline.
 * If headline and description would be identical, returns null for description.
 * 
 * @param justification - Full justification text from root_causes
 * @param headline - Extracted headline (if any)
 * @returns Full justification text, or null if identical to headline
 */
export function extractDescription(
  justification: string | null | undefined,
  headline: string | null | undefined
): string | null {
  if (!justification || typeof justification !== 'string' || !justification.trim()) {
    return null;
  }

  const trimmed = justification.trim();

  // If no headline, return full justification
  if (!headline || !headline.trim()) {
    return trimmed;
  }

  // Normalize for comparison (remove punctuation, lowercase, trim)
  const headlineNormalized = headline
    .replace(/[.!?]+/g, '')
    .toLowerCase()
    .trim();
  
  const descriptionNormalized = trimmed
    .replace(/[.!?]+/g, '')
    .toLowerCase()
    .trim();

  // If they're identical (or headline is contained in description as first sentence), return null
  // This prevents showing the same text twice
  if (headlineNormalized === descriptionNormalized || 
      descriptionNormalized.startsWith(headlineNormalized)) {
    // If description is just the headline, return null to avoid duplication
    if (descriptionNormalized.length <= headlineNormalized.length + 10) {
      return null;
    }
  }

  return trimmed;
}

